/**
 * digitaltwins-service#576: the rows of /review, built as DOM nodes. Values from the
 * server (mission, user, score) and the review route kept in localStorage are never
 * parsed as HTML: they become text nodes, and the review link is built only for a
 * route that opens a run in review mode.
 */

/**
 * Routes that open a run in review mode (they read mode, id, username and lang).
 * The removed /mars and /competition are not among them (#576).
 */
export const REVIEW_ROUTES: readonly string[] = ['/experience'];

export type ReviewRecord = {
  id?: unknown;
  ojectpk?: unknown;
  nguoitao?: unknown;
  diem_datduoc?: unknown;
  ngaytao?: unknown;
};

/**
 * The review link of `record`, or null. `rawView` is the route saved in localStorage
 * (`url_view`): it must be a non-empty string naming, on this origin, exactly one of
 * REVIEW_ROUTES, with no query or fragment of its own. The record's parameters are
 * encoded by URLSearchParams. The API gives no route per record, so the link is the
 * supported route, not necessarily the one the run was made on.
 */
export function reviewLink(rawView: unknown, record: ReviewRecord, lang: unknown, origin: string): string | null {
  if (typeof rawView !== 'string' || !rawView) return null;
  let url: URL;
  let home: URL;
  try {
    home = new URL(origin);
    url = new URL(rawView, home);
  } catch {
    return null;
  }
  if (url.origin !== home.origin || url.search || url.hash) return null;
  if (!REVIEW_ROUTES.includes(url.pathname)) return null;
  const params = new URLSearchParams();
  params.set('id', text(record.id));
  params.set('username', text(record.nguoitao));
  params.set('lang', typeof lang === 'string' ? lang : '');
  params.set('mode', 'review');
  return `${url.origin}${url.pathname}?${params.toString()}`;
}

/** One table row; the last cell is the link, or `labels.unavailable` as text. */
export function reviewRow(
  doc: Document,
  index: number,
  record: ReviewRecord,
  link: string | null,
  labels: { review: string; unavailable: string },
): HTMLTableRowElement {
  const row = doc.createElement('tr');
  const cell = (value: string) => {
    const td = doc.createElement('td');
    td.textContent = value;
    row.appendChild(td);
    return td;
  };
  cell(String(index + 1));
  cell(text(record.ojectpk));
  cell(text(record.nguoitao));
  cell(text(record.diem_datduoc));
  cell(runDate(record.ngaytao));
  const last = cell('');
  if (link) {
    const a = doc.createElement('a');
    a.setAttribute('href', link);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener');
    a.textContent = labels.review;
    last.appendChild(a);
  } else {
    last.textContent = labels.unavailable;
  }
  return row;
}

function text(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

/** dd/mm/yyyy  hh:mm of a Unix time in seconds, as the page showed it; '' if not a number. */
function runDate(seconds: unknown): string {
  const n = Number(seconds);
  if (seconds === null || seconds === '' || !Number.isFinite(n)) return '';
  const d = new Date(n * 1000);
  const two = (v: number) => v.toString().padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}  ${two(d.getHours())}:${two(d.getMinutes())}`;
}
